/**
 * Build Your Own Box — IYLA Collective
 * Handles product add/remove via the Shopify AJAX Cart API and drives the
 * sticky progress bar. The discount itself is NOT calculated here — it's
 * applied by a Monk automatic discount that checks for (a) 3+ items from
 * the featured collection AND (b) one of two "gatekeeper" gift products
 * in the cart. Those gift products are unlisted, so the only way they
 * ever end up in a cart is through this script — that's what makes the
 * discount unreachable from a regular product page.
 */
(function () {
  'use strict';

  function initBox(root) {
    var required = parseInt(root.dataset.required, 10) || 3;
    var discount = root.dataset.discount || '15';
    var sectionKey = 'iylaByob:' + root.id;
    var giftKey = sectionKey + ':gift';

    var cards = root.querySelectorAll('[data-byob-card]');
    var fillEl = root.querySelector('[data-byob-fill]');
    var textEl = root.querySelector('[data-byob-text]');
    var barWrapper = root.querySelector('[data-byob-bar]');
    var cartBtns = root.querySelectorAll('[data-byob-cart-btn]');
    var errorEl = root.querySelector('[data-byob-error]');
    var giftOptionEls = root.querySelectorAll('[data-byob-gift-option]');

    var selected = loadSelection();
    var selectedGiftVariantId = loadGiftSelection() || defaultGiftVariantId();
    var giftPresentVariantId = null; // which gift variant is actually sitting in the cart right now

    function loadSelection() {
      try {
        var raw = sessionStorage.getItem(sectionKey);
        return raw ? new Set(JSON.parse(raw)) : new Set();
      } catch (e) {
        return new Set();
      }
    }

    function saveSelection() {
      try {
        sessionStorage.setItem(sectionKey, JSON.stringify(Array.from(selected)));
      } catch (e) {
        /* sessionStorage unavailable — non-fatal, state just won't persist on reload */
      }
    }

    function loadGiftSelection() {
      try {
        return sessionStorage.getItem(giftKey) || null;
      } catch (e) {
        return null;
      }
    }

    function saveGiftSelection() {
      try {
        if (selectedGiftVariantId) {
          sessionStorage.setItem(giftKey, selectedGiftVariantId);
        } else {
          sessionStorage.removeItem(giftKey);
        }
      } catch (e) {
        /* non-fatal */
      }
    }

    function defaultGiftVariantId() {
      return giftOptionEls.length ? giftOptionEls[0].dataset.variantId : null;
    }

    function markGiftOptionSelected() {
      giftOptionEls.forEach(function (el) {
        var isSelected = el.dataset.variantId === selectedGiftVariantId;
        el.setAttribute('data-byob-gift-selected', isSelected ? 'true' : 'false');
      });
    }

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.setAttribute('data-byob-active', 'true');
      window.clearTimeout(errorEl._byobTimeout);
      errorEl._byobTimeout = window.setTimeout(function () {
        errorEl.removeAttribute('data-byob-active');
      }, 4000);
    }

    function updateUI() {
      var count = selected.size;
      var pct = Math.min(100, Math.round((count / required) * 100));
      if (fillEl) fillEl.style.width = pct + '%';

      if (textEl) {
        if (count >= required) {
          textEl.textContent = count + ' of ' + required + ' added — ' + discount + '% off unlocked';
          textEl.setAttribute('data-byob-unlocked', 'true');
        } else {
          var remaining = required - count;
          textEl.textContent = count + ' of ' + required + ' added — add ' + remaining + ' or more to unlock ' + discount + '% off';
          textEl.removeAttribute('data-byob-unlocked');
        }
      }

      cartBtns.forEach(function (btn) {
        btn.disabled = count === 0;
      });

      if (barWrapper) {
        barWrapper.setAttribute('data-byob-visible', count > 0 ? 'true' : 'false');
      }

      cards.forEach(function (card) {
        var variantId = card.dataset.variantId;
        var isSelected = selected.has(variantId);
        card.setAttribute('data-byob-selected', isSelected ? 'true' : 'false');

        var variantSelect = card.querySelector('[data-byob-variant-select]');
        if (variantSelect) variantSelect.disabled = isSelected;
      });

      markGiftOptionSelected();
    }

    function addGiftVariant(variantId) {
      if (!variantId) return Promise.resolve();
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      }).then(function (res) {
        if (res.ok) giftPresentVariantId = variantId;
      }).catch(function () {
        /* non-fatal: box items are already in cart even if the gift add fails */
      });
    }

    function removeGiftVariant(variantId) {
      if (!variantId) return Promise.resolve();
      return fetch('/cart.js')
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          var line = cart.items.findIndex(function (item) {
            return String(item.variant_id) === String(variantId);
          });
          if (line === -1) return null;
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: line + 1, quantity: 0 })
          });
        })
        .then(function () {
          if (giftPresentVariantId === variantId) giftPresentVariantId = null;
        })
        .catch(function () { /* non-fatal */ });
    }

    // Ensures whatever gift is currently selected matches what's actually
    // in the cart. Called after every box add/remove, and whenever the
    // shopper switches their gift choice mid-build.
    function syncGiftToCart() {
      var wantedVariantId = selected.size > 0
        ? (selectedGiftVariantId || defaultGiftVariantId())
        : null;

      if (wantedVariantId === giftPresentVariantId) return Promise.resolve();

      var steps = Promise.resolve();
      if (giftPresentVariantId) {
        steps = steps.then(function () { return removeGiftVariant(giftPresentVariantId); });
      }
      if (wantedVariantId) {
        steps = steps.then(function () { return addGiftVariant(wantedVariantId); });
      }
      return steps;
    }

    function addToCart(variantId) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      }).then(function (res) {
        if (!res.ok) {
          return res.json().then(function (data) {
            throw new Error(data.description || 'Could not add that item right now.');
          });
        }
        return res.json();
      });
    }

    function removeFromCart(variantId) {
      return fetch('/cart.js')
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          var line = cart.items.findIndex(function (item) {
            return String(item.variant_id) === String(variantId);
          });
          if (line === -1) return null;
          var currentQty = cart.items[line].quantity;
          return fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ line: line + 1, quantity: currentQty - 1 })
          }).then(function (res) { return res.json(); });
        });
    }

    root.addEventListener('change', function (event) {
      var select = event.target.closest('[data-byob-variant-select]');
      if (!select) return;

      var card = select.closest('[data-byob-card]');
      if (!card) return;

      var option = select.options[select.selectedIndex];
      var newVariantId = option.value;

      card.dataset.variantId = newVariantId;

      var addBtn = card.querySelector('[data-byob-add]');
      if (addBtn) addBtn.dataset.variantId = newVariantId;

      var priceEl = card.querySelector('[data-byob-price]');
      if (priceEl && option.dataset.priceFormatted) {
        priceEl.textContent = option.dataset.priceFormatted;
      }
    });

    root.addEventListener('click', function (event) {
      var giftOptionBtn = event.target.closest('[data-byob-gift-option]');
      if (giftOptionBtn) {
        var newGiftVariantId = giftOptionBtn.dataset.variantId;
        if (newGiftVariantId === selectedGiftVariantId) return;

        selectedGiftVariantId = newGiftVariantId;
        saveGiftSelection();
        markGiftOptionSelected();

        // If the box already has items (and therefore a gift already in
        // cart), swap it live rather than waiting for the next add/remove.
        if (selected.size > 0) {
          syncGiftToCart().catch(function () {
            showError('Could not switch your gift right now. Please try again.');
          });
        }
        return;
      }

      var btn = event.target.closest('[data-byob-add]');
      if (btn) {
        var variantId = btn.dataset.variantId;
        var isSelected = selected.has(variantId);
        btn.disabled = true;

        var action = isSelected ? removeFromCart(variantId) : addToCart(variantId);

        action
          .then(function () {
            if (isSelected) {
              selected.delete(variantId);
            } else {
              selected.add(variantId);
            }
            saveSelection();
            return syncGiftToCart();
          })
          .then(function () {
            updateUI();
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          })
          .catch(function (err) {
            showError(err.message || 'Something went wrong. Please try again.');
          })
          .finally(function () {
            btn.disabled = false;
          });
        return;
      }

      var cartBtn = event.target.closest('[data-byob-cart-btn]');
      if (cartBtn && !cartBtn.disabled) {
        window.location.href = '/cart';
      }
    });

    updateUI();
  }

  function init() {
    document.querySelectorAll('[data-iyla-byob]').forEach(initBox);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
